import { zodResolver } from "@hookform/resolvers/zod";
import { fieldErrorsOf } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import type { ServiceRequest } from "./scheduleApi";
import { serviceFormSchema, type ServiceFormValues } from "./scheduleRules";

export interface ServiceFormProps {
  /** Submit the service (create). Rejects with an ApiError whose 422 fieldErrors land on the fields. */
  onSubmit: (body: ServiceRequest) => Promise<void>;
  /** SC-3 echo of the defaults the last save stamped (30 min / 2 h / 60 d) — shown to the manager (R3). */
  defaultsNotice?: string | null;
}

/**
 * The service create form (ADR-0027: zod → react-hook-form → controlled inputs). Client validation
 * mirrors R7/SC-2 (buffer ≥ 0, cap > 0) and the party range; the SERVER is authoritative — a 422 maps
 * its `fieldErrors` back onto the same fields via `fieldErrorsOf` + `setError`. Enabling online-booking
 * lets the server stamp the SC-3 slot-grid defaults, echoed via [defaultsNotice].
 */
export function ServiceForm({ onSubmit, defaultsNotice }: ServiceFormProps) {
  const { t } = useTranslation();
  const schema = useMemo(() => serviceFormSchema(t), [t]);
  const form = useForm<ServiceFormValues>({
    // z.coerce.* makes the schema's INPUT type `unknown`, which trips the resolver's generic; the
    // OUTPUT type (ServiceFormValues) is what the form yields, so we pin the resolver to it.
    resolver: zodResolver(schema) as Resolver<ServiceFormValues>,
    defaultValues: {
      nameFr: "",
      partyMin: 1,
      partyMax: 8,
      onlineBookable: false,
      turnoverBufferMinutes: 0,
    },
    mode: "onSubmit",
  });

  const submit = form.handleSubmit(async (data) => {
    try {
      await onSubmit({
        nameFr: data.nameFr,
        partyMin: data.partyMin,
        partyMax: data.partyMax,
        onlineBookable: data.onlineBookable,
        turnoverBufferMinutes: data.turnoverBufferMinutes,
        coversCapPerSlot: data.coversCapPerSlot ?? null,
      });
      form.reset();
    } catch (err) {
      for (const [field, message] of Object.entries(fieldErrorsOf(err))) {
        // Map server field names to form fields where they differ.
        const mapped =
          field === "coversCapPerSlot" ||
          field === "turnoverBufferMinutes" ||
          field.startsWith("party") ||
          field.startsWith("name")
            ? (field as keyof ServiceFormValues)
            : "nameFr";
        form.setError(mapped, { type: "server", message });
      }
    }
  });

  const err = (name: keyof ServiceFormValues) =>
    form.formState.errors[name]?.message as string | undefined;

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "xl" })}
    >
      <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
        {t("schedule.service.title")}
      </h3>

      <Field label={t("schedule.service.name")} error={err("nameFr")}>
        <input {...form.register("nameFr")} className={inputStyle} />
      </Field>
      <div className={css({ display: "flex", gap: "3" })}>
        <Field label={t("schedule.service.partyMin")} error={err("partyMin")}>
          <input type="number" min={1} {...form.register("partyMin")} className={inputStyle} />
        </Field>
        <Field label={t("schedule.service.partyMax")} error={err("partyMax")}>
          <input type="number" min={1} {...form.register("partyMax")} className={inputStyle} />
        </Field>
      </div>
      <div className={css({ display: "flex", gap: "3" })}>
        <Field
          label={t("schedule.service.buffer")}
          hint={t("schedule.service.bufferHint")}
          error={err("turnoverBufferMinutes")}
        >
          <input
            type="number"
            min={0}
            {...form.register("turnoverBufferMinutes")}
            className={inputStyle}
          />
        </Field>
        <Field
          label={t("schedule.service.cap")}
          hint={t("schedule.service.capHint")}
          error={err("coversCapPerSlot")}
        >
          <input
            type="number"
            min={1}
            {...form.register("coversCapPerSlot")}
            className={inputStyle}
          />
        </Field>
      </div>
      <label className={css({ display: "flex", gap: "2", alignItems: "center", fontSize: "sm" })}>
        <input type="checkbox" {...form.register("onlineBookable")} />
        {t("schedule.service.onlineBookable")}
      </label>

      {defaultsNotice ? (
        <output className={css({ display: "block", fontSize: "sm", color: "fg.muted" })}>
          {defaultsNotice}
        </output>
      ) : null}

      <button
        type="submit"
        disabled={form.formState.isSubmitting}
        className={css({
          alignSelf: "flex-start",
          px: "4",
          py: "2",
          rounded: "md",
          bg: "colorPalette.default",
          color: "colorPalette.fg",
        })}
      >
        {t("schedule.service.create")}
      </button>
    </form>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "1", flex: "1" })}>
      <label className={css({ fontSize: "sm" })}>{label}</label>
      {hint ? <span className={css({ color: "fg.muted", fontSize: "xs" })}>{hint}</span> : null}
      {children}
      {error ? <span className={css({ color: "fg.error", fontSize: "xs" })}>{error}</span> : null}
    </div>
  );
}

const inputStyle = css({ px: "3", py: "2", borderWidth: "1px", rounded: "md", w: "full" });
